import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { ShipmentDocument } from '../types/shipment';

const BUCKET = 'shipment-docs';

const mapDoc = (row: {
  id: string;
  shipment_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  content_type: string | null;
  uploaded_at: string;
}): ShipmentDocument => ({
  id: row.id,
  shipmentId: row.shipment_id,
  fileName: row.file_name,
  filePath: row.file_path,
  fileSize: row.file_size ?? undefined,
  contentType: row.content_type ?? undefined,
  uploadedAt: row.uploaded_at,
});

export async function listShipmentDocuments(shipmentId: string): Promise<ShipmentDocument[]> {
  if (!isSupabaseConfigured()) return [];
  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipment_documents')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('uploaded_at', { ascending: false });

  if (error || !data) return [];
  return data.map(mapDoc);
}

export async function uploadShipmentDocument(
  shipmentId: string,
  file: File
): Promise<ShipmentDocument | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabase()!;
  const safeName = file.name.replace(/[^\w.\-()\u0600-\u06FF]+/g, '_');
  const path = `${shipmentId}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });

  if (uploadError) {
    console.error(uploadError);
    return null;
  }

  const { data, error } = await supabase
    .from('shipment_documents')
    .insert({
      shipment_id: shipmentId,
      file_name: file.name,
      file_path: path,
      file_size: file.size,
      content_type: file.type || null,
    })
    .select('*')
    .single();

  if (error || !data) return null;
  return mapDoc(data);
}

export async function getDocumentDownloadUrl(filePath: string): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabase()!;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(filePath, 60 * 10);

  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function deleteShipmentDocument(doc: ShipmentDocument): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  const supabase = getSupabase()!;
  await supabase.storage.from(BUCKET).remove([doc.filePath]);
  const { error } = await supabase.from('shipment_documents').delete().eq('id', doc.id);
  return !error;
}
